import * as z from "zod/v4";
import { ZodType } from "zod/v4";
/**
 * Utility function to retrieve and parse an item using a Zod schema
 * with standardized error handling. This follows a common pattern used throughout the codebase:
 * 1. Check if item exists
 * 2. Parse it using a Zod schema
 * 3. Return the parsed data if successful
 *
 * @param item The item to check and parse
 * @param schema The Zod schema to use for parsing
 * @param itemDescription A description of the item for error messages (e.g., "customer input message")
 * @param idOrIdentifier An ID or identifier for the item in error messages
 * @returns The parsed item
 * @throws Error if the item doesn't exist or fails to parse
 */
export function parseItem<T extends ZodType>(
  item: unknown | null | undefined,
  schema: T,
  itemDescription?: string,
  idOrIdentifier?: string,
): z.infer<T> {
  if (!item) {
    throw new Error(
      `Failed to find ${itemDescription} with id ${idOrIdentifier}`,
    );
  }

  const parsed = schema.safeParse(item);
  if (!parsed.success) {
    throw new Error(
      `Failed to parse ${itemDescription} with id ${idOrIdentifier}: ${parsed.error.message}`,
    );
  }

  return parsed.data;
}
